import { httpsCallable } from 'firebase/functions';
import {
  doc,
  getDoc,
  collection,
  query,
  where,
  orderBy,
  getDocs,
  limit,
  setDoc,
  deleteDoc,
  updateDoc,
  writeBatch,
  onSnapshot,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { functions, db, storage, auth } from '../firebase/config/firebase';
import { invalidateOccupiedSeatsCache } from './tripService';
import { invalidateAdminBookingsCache } from './adminBookingService';
import { compressPaymentProof } from '../utils/imageCompressor';
import {
  CreatePaymentAttemptInput,
  CreatePaymentAttemptResult,
  VerifyPaymentInput,
  VerifyPaymentResult,
  PaymentAttempt,
  Booking,
  BookingGroup,
  ApiResponse,
  PaymentSettings,
  ManualPaymentSubmissionInput,
} from '../types';
import { mapFunctionsError, createError } from '../utils/errors';

export const DEFAULT_CLIENT_PAYMENT_SETTINGS: PaymentSettings = {
  instapayEnabled: true,
  instapayRecipient: 'OTO STUDENTS',
  instapayNumber: '01099887766',
  instapayInstructions:
    'يرجى تحويل المبلغ المطلوب عبر تطبيق إنستاباي والتأكد من اسم المستفيد قبل إتمام التحويل، ثم رفع صورة إشعار التحويل مع إدخال الرقم المحول منه.',

  walletEnabled: true,
  walletProviders: ['VODAFONE_CASH', 'ORANGE_CASH', 'ETISALAT_CASH', 'WE_PAY'],
  walletNumber: '01012345678',
  walletRecipient: 'OTO STUDENTS',
  walletInstructions:
    'قم بتحويل قيمة التذكرة عبر محفظتك الإلكترونية إلى الرقم الموضح، واحتفظ برسالة أو إشعار تأكيد التحويل لرفعه كإثبات سداد.',

  branchPaymentEnabled: true,
  branchName: 'مكتب OTO - فرع سيدي جابر',
  branchAddress: 'الإسكندرية، محطة سيدي جابر، شارع المشير، عمارة الأمل الدور الأول',
  branchPhone: '01000000000',
  branchWorkingHours: 'يوميًا من 8:00 صباحًا حتى 6:00 مساءً',
  branchInstructions:
    'تفضل بزيارة الفرع قبل موعد رحلتك بـ 24 ساعة على الأقل لسداد قيمة التذكرة نقدًا واستلام الإيصال الورقي وتفعيل التذكرة الإلكترونية.',
};

export function isGenericStudentName(name?: string | null): boolean {
  if (!name) return true;
  const trimmed = name.trim();
  return (
    trimmed === '' ||
    trimmed === '—' ||
    trimmed === 'طالب OTO' ||
    trimmed === 'طالب OTO المعتمد' ||
    trimmed === 'طالب oto' ||
    trimmed === 'طالب' ||
    trimmed === 'طالب معتمد'
  );
}

// In-memory caches to eliminate redundant Firestore queries and prevent server slowdown
const studentProfileCache = new Map<string, { fullName: string; phoneNumber?: string; universityName?: string }>();
const bookingGroupCache = new Map<string, any>();
const bookingCache = new Map<string, any>();
let cachedPaymentAttempts: { data: PaymentAttempt[]; filter?: string; timestamp: number } | null = null;

export const invalidatePaymentAttemptsCache = () => {
  cachedPaymentAttempts = null;
};

// Timeout helper to prevent infinite hanging on mobile or flaky network
const withTimeout = <T>(promise: Promise<T>, ms = 7000, errorMsg = 'انتهت مهلة استجابة الخادم'): Promise<T> => {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) => setTimeout(() => reject(new Error(errorMsg)), ms)),
  ]);
};

// Detect Firebase 429 Quota Exceeded errors
const isQuotaError = (err: any): boolean => {
  const code = String(err?.code || '').toLowerCase();
  const msg = String(err?.message || '').toLowerCase();
  const status = String(err?.status || '').toLowerCase();
  return (
    code.includes('resource-exhausted') ||
    code.includes('quota') ||
    code.includes('429') ||
    status.includes('resource_exhausted') ||
    msg.includes('quota exceeded') ||
    msg.includes('resource_exhausted') ||
    msg.includes('exceeded its quota')
  );
};


/**
 * CLIENT PAYMENT API — SECURE BRIDGE TO SERVER-SIDE PAYMENT ENGINE
 *
 * Supports:
 * - Manual Payments: InstaPay, Mobile Wallet, Pay at Branch
 * - Admin Review & Verification: Approve / Reject
 * - Payment Settings Management
 * - Legacy Online Gateway (Paymob / Simulation) preserved
 */
export const paymentApi = {
  invalidateAttemptsCache: invalidatePaymentAttemptsCache,
  /**
   * 1. Create a server-authoritative online payment attempt (Legacy / Phase 2)
   */
  async createPaymentAttempt(
    input: CreatePaymentAttemptInput
  ): Promise<ApiResponse<CreatePaymentAttemptResult>> {
    try {
      const callable = httpsCallable<CreatePaymentAttemptInput, CreatePaymentAttemptResult>(
        functions,
        'createPaymentAttempt'
      );
      const res = await callable(input);
      return { success: true, data: res.data };
    } catch (err: unknown) {
      console.error('createPaymentAttempt error:', err);
      return { success: false, error: mapFunctionsError(err) };
    }
  },

  /**
   * 2. Authoritative online payment status verification (Legacy / Phase 2)
   */
  async verifyPayment(
    input: VerifyPaymentInput
  ): Promise<ApiResponse<VerifyPaymentResult>> {
    try {
      const callable = httpsCallable<VerifyPaymentInput, VerifyPaymentResult>(
        functions,
        'verifyPayment'
      );
      const res = await callable(input);
      return { success: true, data: res.data };
    } catch (err: unknown) {
      console.error('verifyPayment error:', err);
      return { success: false, error: mapFunctionsError(err) };
    }
  },

  /**
   * 3. Submit Manual Payment Proof (InstaPay, Wallet, Branch)
   */
  async submitManualPayment(
    input: ManualPaymentSubmissionInput
  ): Promise<ApiResponse<{ bookingId?: string; bookingGroupId?: string; paymentAttemptId: string; status: string; messageAr: string }>> {
    try {
      const attemptId = `ATT-${Date.now().toString(36).toUpperCase()}`;
      const currentUid = auth.currentUser?.uid || 'student';

      let studentName = input.studentName?.trim() || '';
      let studentPhone = input.studentPhone?.trim() || '';
      let universityName = '';
      let tripDate = '';
      let seatNumber: any = '';
      let amount = 0;
      let bookingCode = '';
      let bookingGroupCode = '';

      if (input.bookingGroupId) {
        try {
          const gSnap = await getDoc(doc(db, 'bookingGroups', input.bookingGroupId));
          if (gSnap.exists()) {
            const g = gSnap.data() as any;
            bookingGroupCache.set(input.bookingGroupId, g);
            const gName = g.studentSnapshot?.fullName;
            if (!studentName && gName && !isGenericStudentName(gName)) {
              studentName = gName;
            }
            if (!studentPhone) {
              studentPhone = g.studentSnapshot?.phoneNumber || g.studentSnapshot?.phone || '';
            }
            universityName = g.studentSnapshot?.universityName || g.universityName || '';
            tripDate = g.tripDates?.join(' ، ') || g.bookings?.map((b: any) => b.date).join(' ، ') || '';
            seatNumber = g.bookings?.map((b: any) => '#' + b.seatNumber).join(', ') || (g.itemCount ? `${g.itemCount} مقاعد` : '');
            amount = g.totalAmount || g.price || 0;
            bookingGroupCode = g.groupCode || '';
            bookingCode = g.groupCode || '';
          }
        } catch (e) {
          console.warn('Error reading bookingGroup in submitManualPayment:', e);
        }
      } else if (input.bookingId) {
        try {
          const bSnap = await getDoc(doc(db, 'bookings', input.bookingId));
          if (bSnap.exists()) {
            const b = bSnap.data() as any;
            bookingCache.set(input.bookingId, b);
            const bName = b.studentSnapshot?.fullName || b.studentName;
            if (!studentName && bName && !isGenericStudentName(bName)) {
              studentName = bName;
            }
            if (!studentPhone) {
              studentPhone = b.studentSnapshot?.phoneNumber || b.phoneNumber || '';
            }
            universityName = b.universityName || '';
            tripDate = b.tripDate || '';
            seatNumber = b.seatNumber || '';
            amount = b.price || 0;
            bookingCode = b.bookingCode || '';
          }
        } catch (e) {
          console.warn('Error reading booking in submitManualPayment:', e);
        }
      }

      // Check current user displayName
      if (!studentName || isGenericStudentName(studentName)) {
        const authDisplayName = auth.currentUser?.displayName?.trim();
        if (authDisplayName && !isGenericStudentName(authDisplayName)) {
          studentName = authDisplayName;
        }
      }

      // Check client-side cached profile name
      if (!studentName || isGenericStudentName(studentName)) {
        if (typeof window !== 'undefined') {
          const localStored = localStorage.getItem('oto_current_student_name');
          if (localStored && !isGenericStudentName(localStored)) {
            studentName = localStored.trim();
          }
        }
      }

      // Check in-memory cache
      if ((!studentName || isGenericStudentName(studentName)) && currentUid) {
        const cached = studentProfileCache.get(currentUid);
        if (cached && cached.fullName && !isGenericStudentName(cached.fullName)) {
          studentName = cached.fullName;
          if (!studentPhone && cached.phoneNumber) studentPhone = cached.phoneNumber;
        }
      }

      // Resolve from Firestore students/{uid} if still generic
      const targetUid = currentUid && currentUid !== 'student' ? currentUid : null;
      if ((!studentName || isGenericStudentName(studentName) || !studentPhone) && targetUid) {
        try {
          const sSnap = await getDoc(doc(db, 'students', targetUid));
          if (sSnap.exists()) {
            const sData = sSnap.data() as any;
            if (sData.fullName && !isGenericStudentName(sData.fullName)) {
              studentName = sData.fullName.trim();
              if (typeof window !== 'undefined') {
                localStorage.setItem('oto_current_student_name', studentName);
              }
              studentProfileCache.set(targetUid, {
                fullName: studentName,
                phoneNumber: sData.phoneNumber || studentPhone,
                universityName: sData.universityName || universityName,
              });
            }
            if (!studentPhone && sData.phoneNumber) {
              studentPhone = sData.phoneNumber.trim();
            }
            if (!universityName && sData.universityName) {
              universityName = sData.universityName;
            }
          }
        } catch (e) {}
      }

      if (!studentName || isGenericStudentName(studentName)) {
        studentName = studentPhone ? `طالب (${studentPhone})` : (bookingCode ? `طالب ${bookingCode}` : 'طالب مسجل');
      }

      const finalProofUrl = input.proofImageUrl || input.proofImageBase64 || null;
      const finalProofBase64 = input.proofImageBase64 || (input.proofImageUrl && input.proofImageUrl.startsWith('data:') ? input.proofImageUrl : null);

      const attemptData: any = {
        id: attemptId,
        studentId: currentUid,
        studentName,
        studentPhone,
        universityName,
        tripDate,
        seatNumber,
        amount,
        bookingCode,
        bookingGroupCode,
        bookingId: input.bookingId || null,
        bookingGroupId: input.bookingGroupId || null,
        paymentMethod: input.paymentMethod,
        walletProvider: input.walletProvider || null,
        transferFrom: input.transferFrom || null,
        transferReference: input.transferReference || null,
        proofImageUrl: finalProofUrl,
        proofImageBase64: finalProofBase64,
        proofStoragePath: input.proofStoragePath || null,
        status: input.paymentMethod === 'BRANCH' ? 'AWAITING_BRANCH_PAYMENT' : 'PENDING_VERIFICATION',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      try {
        await setDoc(doc(db, 'paymentAttempts', attemptId), attemptData, { merge: true });
        if (input.bookingGroupId) {
          await setDoc(
            doc(db, 'bookingGroups', input.bookingGroupId),
            {
              paymentStatus: 'PENDING_VERIFICATION',
              status: 'PENDING',
              paymentAttemptId: attemptId,
              paymentMethod: input.paymentMethod,
              transferFrom: input.transferFrom || null,
              proofImageUrl: finalProofUrl,
              proofImageBase64: finalProofBase64,
              updatedAt: new Date().toISOString(),
            },
            { merge: true }
          );

          // Also update all child bookings of this group
          try {
            const gSnap = await getDoc(doc(db, 'bookingGroups', input.bookingGroupId));
            if (gSnap.exists()) {
              const childIds = gSnap.data().bookingIds || (gSnap.data().bookings || []).map((b: any) => b.bookingId).filter(Boolean);
              for (const cId of childIds) {
                await setDoc(
                  doc(db, 'bookings', cId),
                  {
                    paymentStatus: 'PENDING_VERIFICATION',
                    status: 'PENDING_PAYMENT_VERIFICATION',
                    bookingStatus: 'PENDING_PAYMENT_VERIFICATION',
                    paymentAttemptId: attemptId,
                    paymentMethod: input.paymentMethod,
                    transferFrom: input.transferFrom || null,
                    proofImageUrl: finalProofUrl,
                    proofImageBase64: finalProofBase64,
                    updatedAt: new Date().toISOString(),
                  },
                  { merge: true }
                );
              }
            }
          } catch (gErr) {
            console.warn('Could not sync child bookings for group:', gErr);
          }
        }
        if (input.bookingId) {
          await setDoc(
            doc(db, 'bookings', input.bookingId),
            {
              paymentStatus: 'PENDING_VERIFICATION',
              status: 'PENDING_PAYMENT_VERIFICATION',
              bookingStatus: 'PENDING_PAYMENT_VERIFICATION',
              paymentAttemptId: attemptId,
              paymentMethod: input.paymentMethod,
              transferFrom: input.transferFrom || null,
              proofImageUrl: finalProofUrl,
              proofImageBase64: finalProofBase64,
              updatedAt: new Date().toISOString(),
            },
            { merge: true }
          );
        }
      } catch (fsErr) {
        console.warn('Could not write payment attempt to Firestore:', fsErr);
      }

      try {
        if (typeof window !== 'undefined') {
          // 1. Sync dev payment attempts for admin panel
          try {
            const rawAttempts = localStorage.getItem('oto_dev_payment_attempts') || '[]';
            const attemptsList = JSON.parse(rawAttempts);
            // Replace if exists, else unshift
            const existingIdx = attemptsList.findIndex((a: any) => a.id === attemptId);
            if (existingIdx >= 0) {
              attemptsList[existingIdx] = attemptData;
            } else {
              attemptsList.unshift(attemptData);
            }
            localStorage.setItem('oto_dev_payment_attempts', JSON.stringify(attemptsList));
            sessionStorage.setItem('oto_dev_payment_attempts', JSON.stringify(attemptsList));
          } catch (_) {}

          // 2. Sync individual booking
          if (input.bookingId) {
            const raw = sessionStorage.getItem(`nashgo_dev_booking_${input.bookingId}`) || localStorage.getItem(`nashgo_dev_booking_${input.bookingId}`);
            if (raw) {
              const b = JSON.parse(raw);
              b.paymentStatus = 'PENDING_VERIFICATION';
              b.status = 'PENDING_PAYMENT_VERIFICATION';
              b.bookingStatus = 'PENDING_PAYMENT_VERIFICATION';
              b.paymentAttemptId = attemptId;
              b.paymentMethod = input.paymentMethod;
              b.transferFrom = input.transferFrom;
              b.proofImageUrl = input.proofImageUrl;
              sessionStorage.setItem(`nashgo_dev_booking_${input.bookingId}`, JSON.stringify(b));
              localStorage.setItem(`nashgo_dev_booking_${input.bookingId}`, JSON.stringify(b));
            }

            // Also sync in student bookings list array
            try {
              const rawStudentBookings = sessionStorage.getItem('nashgo_dev_student_bookings') || localStorage.getItem('nashgo_dev_student_bookings');
              if (rawStudentBookings) {
                const sBookings = JSON.parse(rawStudentBookings);
                const updatedSBookings = sBookings.map((sb: any) => {
                  if (sb.id === input.bookingId || (bookingCode && sb.bookingCode === bookingCode)) {
                    return {
                      ...sb,
                      paymentStatus: 'PENDING_VERIFICATION',
                      status: 'PENDING_PAYMENT_VERIFICATION',
                      bookingStatus: 'PENDING_PAYMENT_VERIFICATION',
                      paymentAttemptId: attemptId,
                      paymentMethod: input.paymentMethod,
                      transferFrom: input.transferFrom,
                      proofImageUrl: input.proofImageUrl,
                    };
                  }
                  return sb;
                });
                sessionStorage.setItem('nashgo_dev_student_bookings', JSON.stringify(updatedSBookings));
                localStorage.setItem('nashgo_dev_student_bookings', JSON.stringify(updatedSBookings));
              }
            } catch (_) {}
          }

          // 3. Sync booking group
          if (input.bookingGroupId) {
            const rawG = sessionStorage.getItem(`nashgo_dev_group_${input.bookingGroupId}`) || localStorage.getItem(`nashgo_dev_group_${input.bookingGroupId}`);
            if (rawG) {
              const g = JSON.parse(rawG);
              g.paymentStatus = 'PENDING_VERIFICATION';
              g.status = 'PENDING';
              g.paymentAttemptId = attemptId;
              g.paymentMethod = input.paymentMethod;
              sessionStorage.setItem(`nashgo_dev_group_${input.bookingGroupId}`, JSON.stringify(g));
              localStorage.setItem(`nashgo_dev_group_${input.bookingGroupId}`, JSON.stringify(g));
            }
          }
        }
      } catch (e) {
        // Ignored
      }

      return {
        success: true,
        data: {
          bookingId: input.bookingId,
          bookingGroupId: input.bookingGroupId,
          paymentAttemptId: attemptId,
          status: 'PENDING_VERIFICATION',
          messageAr: 'تم إرسال إثبات السداد بنجاح للمراجعة والتدقيق',
        },
      };
    } catch (err: any) {
      console.error('submitManualPayment error:', err);
      return {
        success: false,
        error: createError('FIREBASE_UNAVAILABLE', err?.message || 'فشل حفظ وتأكيد بيانات السداد في قاعدة البيانات'),
      };
    }
  },

  /**
   * 4. Admin Approves Manual Payment (Atomic Batch & Timeout-Protected)
   */
  async approveManualPayment(input: {
    paymentAttemptId: string;
    bookingId?: string;
    bookingGroupId?: string;
    adminNote?: string;
    adminName?: string;
    adminEmail?: string;
    adminUid?: string;
    discountAmount?: number;
  }): Promise<ApiResponse<{ bookingId?: string; bookingGroupId?: string; paymentAttemptId: string; messageAr: string }>> {
    try {
      const nowIso = new Date().toISOString();
      const reviewerName = input.adminName?.trim() || 'إدارة النظام';
      const reviewerEmail = input.adminEmail?.trim() || '';
      const reviewerUid = input.adminUid?.trim() || 'admin';
      const attemptRef = doc(db, 'paymentAttempts', input.paymentAttemptId);
      let attemptData: any = null;

      // 1. Fetch attempt data with 4s timeout
      try {
        const snap = await withTimeout(getDoc(attemptRef), 4000, 'Attempt fetch timeout');
        if (snap.exists()) {
          attemptData = snap.data();
        }
      } catch (_) {}

      if (!attemptData && typeof window !== 'undefined') {
        try {
          const rawAttempts =
            localStorage.getItem('oto_dev_payment_attempts') ||
            sessionStorage.getItem('oto_dev_payment_attempts');
          if (rawAttempts) {
            const list = JSON.parse(rawAttempts);
            attemptData = list.find((a: any) => a.id === input.paymentAttemptId);
          }
        } catch (_) {}
      }

      // Resolve booking group data upfront if present
      let targetGroupId = input.bookingGroupId || attemptData?.bookingGroupId;
      const groupCode = attemptData?.bookingGroupCode;
      let gData: any = null;

      if (!targetGroupId && groupCode) {
        try {
          const gQuery = query(collection(db, 'bookingGroups'), where('groupCode', '==', groupCode), limit(1));
          const gSnap = await withTimeout(getDocs(gQuery), 4000, 'Group query timeout');
          if (!gSnap.empty) {
            targetGroupId = gSnap.docs[0].id;
          }
        } catch (_) {}
      }

      if (targetGroupId) {
        try {
          const gSnap = await withTimeout(getDoc(doc(db, 'bookingGroups', targetGroupId)), 4000, 'Group doc timeout');
          if (gSnap.exists()) {
            gData = gSnap.data();
          }
        } catch (_) {}
      }

      // Compute authoritative original total, ticket count, discount, and unit price
      const originalTotalAmount =
        (gData && gData.totalAmount ? Number(gData.totalAmount) : 0) ||
        Number(attemptData?.amount) ||
        0;

      const rawDiscount = Number(input.discountAmount) || 0;
      const discount = Math.max(0, Math.min(originalTotalAmount, rawDiscount));
      const hasDiscount = discount > 0;
      const finalTotalAmount = Math.max(0, originalTotalAmount - discount);

      // Total tickets count in this group or single order
      const ticketCount = Math.max(
        1,
        gData?.itemCount ||
        gData?.bookings?.length ||
        (attemptData as any)?.tripCount ||
        1
      );

      // Distribute discount equally among all tickets
      const discountPerTicket = hasDiscount ? Number((discount / ticketCount).toFixed(2)) : 0;
      const originalUnitPrice = ticketCount > 0 ? (originalTotalAmount / ticketCount) : originalTotalAmount;
      const finalUnitPrice = Math.max(0, Number((originalUnitPrice - discountPerTicket).toFixed(2)));

      // Initialize atomic batch to execute all writes in a single round-trip
      const batch = writeBatch(db);

      // A. Update payment attempt to PAID (with discount details)
      batch.set(
        attemptRef,
        {
          status: 'PAID',
          amount: finalTotalAmount,
          originalAmount: originalTotalAmount,
          discountAmount: discount,
          discountPerTicket: discountPerTicket,
          discountApplied: hasDiscount,
          verifiedAt: nowIso,
          reviewedAt: nowIso,
          verifiedByName: reviewerName,
          reviewedByName: reviewerName,
          reviewedBy: reviewerUid || reviewerEmail || 'admin',
          reviewedByEmail: reviewerEmail,
          updatedAt: nowIso,
          adminNote: hasDiscount
            ? (input.adminNote ? `${input.adminNote} | ` : '') +
              `تم تطبيق خصم إداري ${discount} ج.م بالتساوي (${discountPerTicket} ج.م/تذكرة)، ليصبح إجمالي التحصيل ${finalTotalAmount} ج.م بواقع ${finalUnitPrice} ج.م لكل تذكرة`
            : (input.adminNote || 'تم اعتماد الدفع بنجاح من لوحة الإدارة'),
        },
        { merge: true }
      );

      // B. Resolve target individual booking
      let targetBookingId = input.bookingId || attemptData?.bookingId;
      const bookingCode = attemptData?.bookingCode;
      if (!targetBookingId && bookingCode) {
        try {
          const bQuery = query(collection(db, 'bookings'), where('bookingCode', '==', bookingCode), limit(1));
          const bSnap = await withTimeout(getDocs(bQuery), 4000, 'Booking query timeout');
          if (!bSnap.empty) {
            targetBookingId = bSnap.docs[0].id;
          }
        } catch (_) {}
      }

      if (targetBookingId) {
        batch.set(
          doc(db, 'bookings', targetBookingId),
          {
            paymentStatus: 'PAID',
            status: 'CONFIRMED',
            bookingStatus: 'CONFIRMED',
            price: finalUnitPrice,
            originalPrice: originalUnitPrice,
            discountApplied: hasDiscount,
            discountAmount: discount,
            paidAt: nowIso,
            verifiedAt: nowIso,
            verifiedByName: reviewerName,
            reviewedByName: reviewerName,
            updatedAt: nowIso,
            adminNote: hasDiscount
              ? `تم اعتماد السداد بعد تطبيق خصم إداري ${discount} ج.م (سعر التذكرة المعتمد: ${finalUnitPrice} ج.م)`
              : 'تم اعتماد السداد وإصدار التذكرة بنجاح',
          },
          { merge: true }
        );

        // Lock single seat on trip
        const singleTripId = attemptData?.tripId;
        const singleSeatNum = attemptData?.seatNumber;
        if (singleTripId && singleSeatNum) {
          const cleanSeat = String(singleSeatNum).replace('#', '').trim();
          batch.set(
            doc(db, 'trips', singleTripId, 'seats', cleanSeat),
            {
              status: 'BOOKED',
              bookingId: targetBookingId,
              studentId: attemptData?.studentId || null,
              updatedAt: nowIso,
            },
            { merge: true }
          );
        }
      }

      // C. Update booking group and child bookings
      if (targetGroupId) {
        const updatedGroupBookings = (gData?.bookings || []).map((sub: any) => ({
          ...sub,
          price: finalUnitPrice,
          originalPrice: sub.price || originalUnitPrice,
          discountApplied: hasDiscount,
          discountAmount: discountPerTicket,
          status: 'CONFIRMED',
          paymentStatus: 'PAID',
        }));

        batch.set(
          doc(db, 'bookingGroups', targetGroupId),
          {
            paymentStatus: 'PAID',
            status: 'CONFIRMED',
            bookingStatus: 'CONFIRMED',
            totalAmount: finalTotalAmount,
            originalTotalAmount: originalTotalAmount,
            discountAmount: discount,
            discountPerTicket: discountPerTicket,
            discountApplied: hasDiscount,
            paidAt: nowIso,
            verifiedAt: nowIso,
            verifiedByName: reviewerName,
            reviewedByName: reviewerName,
            updatedAt: nowIso,
            bookings: updatedGroupBookings,
            adminNote: hasDiscount
              ? (input.adminNote ? `${input.adminNote} | ` : '') +
                `تم اعتماد السداد مع تطبيق خصم إداري ${discount} ج.م بالتساوي (${discountPerTicket} ج لكل تذكرة)، الصافي: ${finalTotalAmount} ج.م`
              : (input.adminNote || 'تم اعتماد السداد وتأكيد الحجز بنجاح'),
          },
          { merge: true }
        );

        // Update child bookings by group ID
        try {
          const bgQuery = query(collection(db, 'bookings'), where('bookingGroupId', '==', targetGroupId));
          const bgSnaps = await withTimeout(getDocs(bgQuery), 4000, 'Child bookings query timeout');
          for (const bDoc of bgSnaps.docs) {
            batch.set(
              bDoc.ref,
              {
                paymentStatus: 'PAID',
                status: 'CONFIRMED',
                bookingStatus: 'CONFIRMED',
                price: finalUnitPrice,
                originalPrice: bDoc.data()?.price || originalUnitPrice,
                discountApplied: hasDiscount,
                discountAmount: discountPerTicket,
                paidAt: nowIso,
                verifiedAt: nowIso,
                verifiedByName: reviewerName,
                reviewedByName: reviewerName,
                updatedAt: nowIso,
                adminNote: hasDiscount
                  ? `تم اعتماد السداد مع خصم إداري (${finalUnitPrice} ج.م للتذكرة)`
                  : (input.adminNote || 'تم اعتماد السداد وإصدار التذكرة بنجاح'),
              },
              { merge: true }
            );
          }
        } catch (_) {}

        // Lock seats for group bookings
        if (gData?.bookings) {
          for (const b of gData.bookings) {
            if (b.tripId && b.seatNumber) {
              const cleanSeat = String(b.seatNumber).replace('#', '').trim();
              batch.set(
                doc(db, 'trips', b.tripId, 'seats', cleanSeat),
                {
                  status: 'BOOKED',
                  bookingId: b.bookingId || null,
                  studentId: gData.studentId || attemptData?.studentId || null,
                  updatedAt: nowIso,
                },
                { merge: true }
              );
            }
          }
        }
      }

      // D. Send instant in-app notification to the student
      const resolvedStudentId = attemptData?.studentId || gData?.studentId || (attemptData as any)?.uid;
      const refCode = bookingCode || groupCode || targetBookingId || targetGroupId || '';
      if (resolvedStudentId) {
        const notifId = `notif_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`;
        batch.set(doc(db, 'notifications', notifId), {
          id: notifId,
          studentId: resolvedStudentId,
          title: hasDiscount ? 'تم اعتماد سداد رحلتك بنجاح مع تطبيق خصم! 🎉' : 'تم اعتماد سداد رحلتك بنجاح! 🎉',
          message: hasDiscount
            ? `تم اعتماد سداد الحجز (${refCode}) بنجاح بمبلغ ${finalTotalAmount} ج.م بعد تطبيق خصم إداري ${discount} ج.م. تذكرتك الرقمية جاهزة ومفعلة برمز QR للركوب.`
            : `تم اعتماد سداد الحجز (${refCode}) بنجاح. تذكرتك الرقمية جاهزة ومفعلة برمز QR للركوب. نتمنى لك رحلة موفقة!`,
          type: 'PAYMENT_APPROVED',
          read: false,
          bookingId: targetBookingId || null,
          bookingGroupId: targetGroupId || null,
          createdAt: nowIso,
          updatedAt: nowIso,
        });
      }

      // E. Automatically register income transaction in Treasury in batch (Discounted Net Amount)
      if (finalTotalAmount > 0) {
        const method = attemptData?.paymentMethod || gData?.paymentMethod || 'INSTAPAY';
        const vault = method === 'INSTAPAY' ? 'INSTAPAY' : method === 'WALLET' ? 'VODAFONE_CASH' : 'CASH_BRANCH';
        const code = attemptData?.bookingGroupCode || attemptData?.bookingCode || gData?.groupCode || targetGroupId || targetBookingId || '';
        const sName = attemptData?.studentName || gData?.studentSnapshot?.fullName || 'طالب';
        const tripCount = ticketCount;
        const txId = `tx_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`;

        const txDescription = targetGroupId
          ? hasDiscount
            ? `اعتماد سداد باقة رحلات (${tripCount} رحلات) للطالب (${sName}) - كود ${code} [بعد خصم إداري ${discount} ج.م بالتساوي (${discountPerTicket} ج.م/تذكرة)، الصافي المعتمد بالخزينة: ${finalTotalAmount} ج.م بواقع ${finalUnitPrice} ج.م للتذكرة]`
            : `اعتماد سداد باقة رحلات (${tripCount} رحلات) للطالب (${sName}) - كود ${code}`
          : hasDiscount
          ? `اعتماد سداد تذكرة رحلة طالب (${sName}) - كود الحجز ${code} [بعد خصم إداري ${discount} ج.م، الصافي المعتمد بالخزينة: ${finalTotalAmount} ج.م]`
          : `اعتماد سداد تذكرة رحلة طالب (${sName}) - كود الحجز ${code}`;

        batch.set(doc(db, 'treasuryTransactions', txId), {
          id: txId,
          type: 'INCOME',
          category: 'BOOKING_PAYMENT',
          categoryLabelAr: targetGroupId ? 'تحصيل حجز باقة رحلات طالب' : 'تحصيل حجز رحلة طالب',
          amount: finalTotalAmount,
          vault: vault as any,
          description: txDescription,
          referenceNumber: attemptData?.transferReference || attemptData?.transferFrom || code,
          recordedBy: reviewerName,
          date: nowIso,
          status: 'CONFIRMED',
          createdAt: nowIso,
        });
      }

      // F. ATOMIC COMMIT (Single network request, protected by 7s timeout)
      await withTimeout(batch.commit(), 7000, 'انتهت مهلة استجابة خادم فايربيز أثناء اعتماد الدفع');

      // G. Local Cache & Session Storage Sync
      if (typeof window !== 'undefined') {
        try {
          if (targetBookingId) {
            const raw = sessionStorage.getItem(`nashgo_dev_booking_${targetBookingId}`) || localStorage.getItem(`nashgo_dev_booking_${targetBookingId}`);
            if (raw) {
              const b = JSON.parse(raw);
              b.paymentStatus = 'PAID';
              b.status = 'CONFIRMED';
              b.bookingStatus = 'CONFIRMED';
              b.price = finalUnitPrice;
              b.originalPrice = originalUnitPrice;
              b.discountApplied = hasDiscount;
              b.discountAmount = discount;
              b.paidAt = nowIso;
              sessionStorage.setItem(`nashgo_dev_booking_${targetBookingId}`, JSON.stringify(b));
              localStorage.setItem(`nashgo_dev_booking_${targetBookingId}`, JSON.stringify(b));
            }
          }

          if (targetGroupId) {
            const rawG = sessionStorage.getItem(`nashgo_dev_group_${targetGroupId}`) || localStorage.getItem(`nashgo_dev_group_${targetGroupId}`);
            if (rawG) {
              const g = JSON.parse(rawG);
              g.paymentStatus = 'PAID';
              g.status = 'CONFIRMED';
              g.bookingStatus = 'CONFIRMED';
              g.totalAmount = finalTotalAmount;
              g.originalTotalAmount = originalTotalAmount;
              g.discountAmount = discount;
              g.discountPerTicket = discountPerTicket;
              g.discountApplied = hasDiscount;
              g.paidAt = nowIso;
              sessionStorage.setItem(`nashgo_dev_group_${targetGroupId}`, JSON.stringify(g));
              localStorage.setItem(`nashgo_dev_group_${targetGroupId}`, JSON.stringify(g));
            }
          }

          const rawAttempts = localStorage.getItem('oto_dev_payment_attempts') || sessionStorage.getItem('oto_dev_payment_attempts');
          if (rawAttempts) {
            const attemptsList = JSON.parse(rawAttempts);
            const updatedAttempts = attemptsList.map((a: any) =>
              a.id === input.paymentAttemptId
                ? {
                    ...a,
                    status: 'PAID',
                    amount: finalTotalAmount,
                    originalAmount: originalTotalAmount,
                    discountAmount: discount,
                    discountPerTicket: discountPerTicket,
                    discountApplied: hasDiscount,
                    verifiedAt: nowIso,
                    reviewedAt: nowIso,
                    verifiedByName: reviewerName,
                    adminNote: hasDiscount
                      ? (input.adminNote ? `${input.adminNote} | ` : '') + `خصم إداري ${discount} ج.م (الصافي ${finalTotalAmount} ج.م)`
                      : (input.adminNote || 'تم اعتماد الدفع'),
                  }
                : a
            );
            localStorage.setItem('oto_dev_payment_attempts', JSON.stringify(updatedAttempts));
            sessionStorage.setItem('oto_dev_payment_attempts', JSON.stringify(updatedAttempts));
          }
        } catch (_) {}
      }

      invalidatePaymentAttemptsCache();
      return {
        success: true,
        data: {
          bookingId: targetBookingId,
          bookingGroupId: targetGroupId,
          paymentAttemptId: input.paymentAttemptId,
          messageAr: 'تم اعتماد الدفع وتأكيد الحجز بنجاح',
        },
      };
    } catch (finalErr: any) {
      console.error('Final approve failed:', finalErr);
      if (isQuotaError(finalErr)) {
        return {
          success: false,
          error: {
            code: 'FIREBASE_UNAVAILABLE' as const,
            messageAr: 'تم استنفاد كوتة الفايربيز اليومية لقاعدة البيانات (Firebase Quota Exceeded / 429). يلزم تفعيل باقة Blaze (الدفع حسب الاستخدام) في Firebase Console للاستمرار بدون توقف.',
            details: 'RESOURCE_EXHAUSTED',
          },
        };
      }
      return {
        success: false,
        error: createError('UNKNOWN_ERROR', finalErr?.message || 'حدث خطأ أثناء اعتماد الدفع'),
      };
    }
  },

  /**
   * 5. Admin Rejects Manual Payment
   */
  async rejectManualPayment(input: {
    paymentAttemptId: string;
    bookingId?: string;
    bookingGroupId?: string;
    rejectionReason: string;
    adminNote?: string;
    adminName?: string;
    adminEmail?: string;
    adminUid?: string;
  }): Promise<ApiResponse<{ bookingId?: string; bookingGroupId?: string; paymentAttemptId: string; messageAr: string }>> {
    try {
      const nowIso = new Date().toISOString();
      const reviewerName = input.adminName?.trim() || 'إدارة النظام';
      const reviewerEmail = input.adminEmail?.trim() || '';
      const reviewerUid = input.adminUid?.trim() || 'admin';
      const attemptRef = doc(db, 'paymentAttempts', input.paymentAttemptId);
      let attemptData: any = null;
      try {
        const snap = await getDoc(attemptRef);
        if (snap.exists()) {
          attemptData = snap.data();
        }
      } catch (_) {}

      // Update payment attempt to REJECTED with setDoc merge
      try {
        await setDoc(
          attemptRef,
          {
            status: 'REJECTED',
            rejectionReason: input.rejectionReason,
            adminNote: input.adminNote || input.rejectionReason,
            rejectedAt: nowIso,
            reviewedAt: nowIso,
            rejectedByName: reviewerName,
            reviewedByName: reviewerName,
            reviewedBy: reviewerUid || reviewerEmail || 'admin',
            reviewedByEmail: reviewerEmail,
            updatedAt: nowIso,
          },
          { merge: true }
        );
      } catch (e) {
        console.warn('Direct setDoc on paymentAttempt failed:', e);
      }

      // Identify target booking ID
      let targetBookingId = input.bookingId || attemptData?.bookingId;
      const bookingCode = attemptData?.bookingCode;

      // If targetBookingId is not found, try to find booking by bookingCode
      if (!targetBookingId && bookingCode) {
        try {
          const bQuery = query(collection(db, 'bookings'), where('bookingCode', '==', bookingCode), limit(1));
          const bSnap = await getDocs(bQuery);
          if (!bSnap.empty) {
            targetBookingId = bSnap.docs[0].id;
          }
        } catch (_) {}
      }

      // Helper function to release seat on a trip safely
      const releaseSeatOnTrip = async (tId?: string | null, sNum?: number | string | null) => {
        if (!tId || sNum === null || sNum === undefined || sNum === '') return;
        try {
          const cleanSeatNum = String(sNum).replace('#', '').trim();
          const seatRef = doc(db, 'trips', tId, 'seats', cleanSeatNum);
          await updateDoc(seatRef, {
            status: 'AVAILABLE',
            bookingId: null,
            studentId: null,
            updatedAt: nowIso,
          });
        } catch (_) {}
        try {
          const tripRef = doc(db, 'trips', tId);
          const tSnap = await getDoc(tripRef);
          if (tSnap && tSnap.exists()) {
            const cur = tSnap.data()?.availableSeats || 0;
            const maxCap = tSnap.data()?.totalSeats || tSnap.data()?.capacity || 50;
            const newAvail = Math.min(maxCap, cur + 1);
            await updateDoc(tripRef, {
              availableSeats: newAvail,
              availableSeatsCount: newAvail,
              status: 'OPEN',
              updatedAt: nowIso,
            });
          }
        } catch (_) {}
      };

      if (targetBookingId) {
        let bTripId = attemptData?.tripId;
        let bSeatNumber = attemptData?.seatNumber;

        try {
          const bSnap = await getDoc(doc(db, 'bookings', targetBookingId));
          if (bSnap.exists()) {
            const bData = bSnap.data();
            bTripId = bTripId || bData.tripId;
            bSeatNumber = bSeatNumber || bData.seatNumber;
          }
        } catch (_) {}

        if (bTripId && bSeatNumber) {
          await releaseSeatOnTrip(bTripId, bSeatNumber);
        }

        try {
          await setDoc(
            doc(db, 'bookings', targetBookingId),
            {
              paymentStatus: 'REJECTED',
              status: 'CANCELLED',
              bookingStatus: 'CANCELLED',
              rejectionReason: input.rejectionReason,
              adminNote: input.adminNote || input.rejectionReason,
              rejectedAt: nowIso,
              rejectedByName: reviewerName,
              reviewedByName: reviewerName,
              updatedAt: nowIso,
            },
            { merge: true }
          );
        } catch (e) {
          console.warn('Direct setDoc on booking failed:', e);
        }

        if (typeof window !== 'undefined') {
          try {
            const raw = sessionStorage.getItem(`nashgo_dev_booking_${targetBookingId}`) || localStorage.getItem(`nashgo_dev_booking_${targetBookingId}`);
            if (raw) {
              const b = JSON.parse(raw);
              b.paymentStatus = 'REJECTED';
              b.status = 'CANCELLED';
              b.bookingStatus = 'CANCELLED';
              b.rejectionReason = input.rejectionReason;
              b.adminNote = input.adminNote || input.rejectionReason;
              b.rejectedAt = nowIso;
              b.rejectedByName = reviewerName;
              b.reviewedByName = reviewerName;
              sessionStorage.setItem(`nashgo_dev_booking_${targetBookingId}`, JSON.stringify(b));
              localStorage.setItem(`nashgo_dev_booking_${targetBookingId}`, JSON.stringify(b));
            }

            // Sync student bookings array
            const rawStudentBookings = sessionStorage.getItem('nashgo_dev_student_bookings') || localStorage.getItem('nashgo_dev_student_bookings');
            if (rawStudentBookings) {
              const sBookings = JSON.parse(rawStudentBookings);
              const updatedSBookings = sBookings.map((sb: any) => {
                if (sb.id === targetBookingId || (bookingCode && sb.bookingCode === bookingCode)) {
                  return {
                    ...sb,
                    paymentStatus: 'REJECTED',
                    status: 'CANCELLED',
                    bookingStatus: 'CANCELLED',
                    rejectionReason: input.rejectionReason,
                    rejectedAt: nowIso,
                    rejectedByName: reviewerName,
                    reviewedByName: reviewerName,
                  };
                }
                return sb;
              });
              sessionStorage.setItem('nashgo_dev_student_bookings', JSON.stringify(updatedSBookings));
              localStorage.setItem('nashgo_dev_student_bookings', JSON.stringify(updatedSBookings));
            }

            // Sync payment attempt in local list
            const rawAttempts = localStorage.getItem('oto_dev_payment_attempts') || sessionStorage.getItem('oto_dev_payment_attempts');
            if (rawAttempts) {
              const attemptsList = JSON.parse(rawAttempts);
              const updatedAttempts = attemptsList.map((a: any) =>
                a.id === input.paymentAttemptId
                  ? {
                      ...a,
                      status: 'REJECTED',
                      rejectionReason: input.rejectionReason,
                      adminNote: input.adminNote || input.rejectionReason,
                      rejectedAt: nowIso,
                      rejectedByName: reviewerName,
                      reviewedByName: reviewerName,
                    }
                  : a
              );
              localStorage.setItem('oto_dev_payment_attempts', JSON.stringify(updatedAttempts));
              sessionStorage.setItem('oto_dev_payment_attempts', JSON.stringify(updatedAttempts));
            }
          } catch (_) {}
        }
      }

      let targetGroupId = input.bookingGroupId || attemptData?.bookingGroupId;
      const groupCode = attemptData?.bookingGroupCode;
      if (!targetGroupId && groupCode) {
        try {
          const gQuery = query(collection(db, 'bookingGroups'), where('groupCode', '==', groupCode), limit(1));
          const gSnap = await getDocs(gQuery);
          if (!gSnap.empty) {
            targetGroupId = gSnap.docs[0].id;
          }
        } catch (_) {}
      }

      if (targetGroupId) {
        try {
          await setDoc(
            doc(db, 'bookingGroups', targetGroupId),
            {
              paymentStatus: 'REJECTED',
              status: 'CANCELLED',
              bookingStatus: 'CANCELLED',
              rejectionReason: input.rejectionReason,
              adminNote: input.adminNote || input.rejectionReason,
              rejectedAt: nowIso,
              rejectedByName: reviewerName,
              reviewedByName: reviewerName,
              updatedAt: nowIso,
            },
            { merge: true }
          );

          // Update child bookings and release their seats
          const gSnap = await getDoc(doc(db, 'bookingGroups', targetGroupId));
          if (gSnap.exists()) {
            const gData = gSnap.data() as any;
            if (Array.isArray(gData.bookings)) {
              for (const sub of gData.bookings) {
                if (sub.tripId && sub.seatNumber) {
                  await releaseSeatOnTrip(sub.tripId, sub.seatNumber);
                }
              }
            }
            const childIds = gData.bookingIds || (gData.bookings || []).map((b: any) => b.bookingId).filter(Boolean);
            for (const cId of childIds) {
              await setDoc(
                doc(db, 'bookings', cId),
                {
                  paymentStatus: 'REJECTED',
                  status: 'CANCELLED',
                  bookingStatus: 'CANCELLED',
                  rejectionReason: input.rejectionReason,
                  adminNote: input.adminNote || input.rejectionReason,
                  rejectedAt: nowIso,
                  rejectedByName: reviewerName,
                  reviewedByName: reviewerName,
                  updatedAt: nowIso,
                },
                { merge: true }
              );
            }
          }
        } catch (e) {
          console.warn('Direct setDoc on bookingGroup failed:', e);
        }
      }

      // Send instant in-app notification to the student about rejection with resubmission action
      const resolvedStudentId = attemptData?.studentId || (attemptData as any)?.uid;
      const refCode = bookingCode || groupCode || targetBookingId || targetGroupId || '';
      const targetPaymentUrl = `/student/payment/${targetGroupId || targetBookingId || ''}`;
      if (resolvedStudentId) {
        try {
          const notifId = `notif_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`;
          const notifMsg = `تم رفض إثبات السداد للحجز (${refCode}) بسبب: ${input.rejectionReason}. يمكنك الآن تعديل بيانات الدفع أو إرفاق إيصال التحويل الصحيح وإعادة إرساله فوراً للمراجعة والاعتماد.`;
          await setDoc(doc(db, 'notifications', notifId), {
            id: notifId,
            studentId: resolvedStudentId,
            title: 'تعذر اعتماد إثبات السداد — يرجى تعديل وإعادة الإرسال ⚠️',
            message: notifMsg,
            body: notifMsg,
            type: 'PAYMENT_REJECTED',
            read: false,
            bookingId: targetBookingId || null,
            bookingGroupId: targetGroupId || null,
            actionUrl: targetPaymentUrl,
            actionLabel: 'تعديل بيانات الدفع والإثبات وإعادة الإرسال ↺',
            rejectionReason: input.rejectionReason,
            data: {
              bookingId: targetBookingId || null,
              bookingGroupId: targetGroupId || null,
              bookingCode: refCode,
              rejectionReason: input.rejectionReason,
              adminNote: input.adminNote || input.rejectionReason,
              targetPaymentUrl,
              canResubmit: true,
              supportPhone: '01207337330',
              supportWhatsapp: '201207337330',
            },
            createdAt: nowIso,
            updatedAt: nowIso,
          });
        } catch (nErr) {
          console.warn('Could not write rejection notification:', nErr);
        }
      }

      invalidatePaymentAttemptsCache();
      return {
        success: true,
        data: {
          bookingId: targetBookingId,
          bookingGroupId: targetGroupId,
          paymentAttemptId: input.paymentAttemptId,
          messageAr: 'تم تسجيل رفض إثبات التحويل وإلغاء الحجز وتحرير المقاعد بنجاح وإشعار الطالب',
        },
      };
    } catch (finalErr: any) {
      console.error('Final reject failed:', finalErr);
      if (isQuotaError(finalErr)) {
        return {
          success: false,
          error: {
            code: 'FIREBASE_UNAVAILABLE' as const,
            messageAr: 'تم استنفاد كوتة الفايربيز اليومية لقاعدة البيانات (Firebase Quota Exceeded / 429). يلزم تفعيل باقة Blaze (الدفع حسب الاستخدام) في Firebase Console للاستمرار بدون توقف.',
            details: 'RESOURCE_EXHAUSTED',
          },
        };
      }
      return {
        success: false,
        error: createError('UNKNOWN_ERROR', finalErr?.message || 'حدث خطأ أثناء رفض العملية'),
      };
    }
  },

  /**
   * 5.1 Admin Deletes Payment Attempt & Fully Purges Reservation
   */
  async deletePaymentAttempt(
    target: string | {
      paymentAttemptId: string;
      bookingId?: string | null;
      bookingGroupId?: string | null;
      bookingCode?: string | null;
      bookingGroupCode?: string | null;
      tripId?: string | null;
      seatNumber?: number | string | null;
      permanent?: boolean;
    }
  ): Promise<ApiResponse<{ messageAr: string }>> {
    const paymentAttemptId = typeof target === 'string' ? target : target.paymentAttemptId;
    let bookingId = typeof target === 'object' ? target.bookingId || null : null;
    let bookingGroupId = typeof target === 'object' ? target.bookingGroupId || null : null;
    let bookingCode = typeof target === 'object' ? target.bookingCode || null : null;
    let bookingGroupCode = typeof target === 'object' ? target.bookingGroupCode || null : null;
    let tripId = typeof target === 'object' ? target.tripId || null : null;
    let seatNumber = typeof target === 'object' ? target.seatNumber || null : null;
    const nowIso = new Date().toISOString();
    const isPermanent = typeof target === 'object' ? !!(target as any).permanent : false;

    try {
      // 1. Immediately extract any matching IDs from localStorage / sessionStorage
      if (typeof window !== 'undefined') {
        try {
          const rawLocal = localStorage.getItem('oto_dev_payment_attempts') || sessionStorage.getItem('oto_dev_payment_attempts');
          if (rawLocal) {
            const list = JSON.parse(rawLocal);
            const found = list.find(
              (a: any) =>
                a.id === paymentAttemptId ||
                (bookingCode && (a.bookingCode === bookingCode || a.bookingGroupCode === bookingCode)) ||
                (bookingId && a.bookingId === bookingId) ||
                (bookingGroupId && a.bookingGroupId === bookingGroupId)
            );
            if (found) {
              bookingId = bookingId || found.bookingId || null;
              bookingGroupId = bookingGroupId || found.bookingGroupId || null;
              bookingCode = bookingCode || found.bookingCode || null;
              bookingGroupCode = bookingGroupCode || found.bookingGroupCode || null;
              tripId = tripId || found.tripId || null;
              seatNumber = seatNumber || found.seatNumber || null;
            }
          }
        } catch (_) {}
      }

      // 2. Storage updates: either permanent purge OR transition to DELETED status
      if (typeof window !== 'undefined') {
        try {
          if (isPermanent) {
            // A) Permanent purge from local storage
            const purgeAttempts = (raw: string | null) => {
              if (!raw) return '[]';
              const list = JSON.parse(raw);
              return JSON.stringify(
                list.filter((a: any) => {
                  if (paymentAttemptId && a.id === paymentAttemptId) return false;
                  if (bookingId && a.bookingId === bookingId) return false;
                  if (bookingGroupId && a.bookingGroupId === bookingGroupId) return false;
                  if (bookingCode && (a.bookingCode === bookingCode || a.bookingGroupCode === bookingCode)) return false;
                  if (bookingGroupCode && (a.bookingGroupCode === bookingGroupCode || a.bookingCode === bookingGroupCode)) return false;
                  return true;
                })
              );
            };
            localStorage.setItem('oto_dev_payment_attempts', purgeAttempts(localStorage.getItem('oto_dev_payment_attempts')));
            sessionStorage.setItem('oto_dev_payment_attempts', purgeAttempts(sessionStorage.getItem('oto_dev_payment_attempts')));

            const purgeBookings = (raw: string | null) => {
              if (!raw) return '[]';
              const list = JSON.parse(raw);
              return JSON.stringify(
                list.filter((b: any) => {
                  if (bookingId && b.id === bookingId) return false;
                  if (bookingGroupId && (b.bookingGroupId === bookingGroupId || b.groupId === bookingGroupId)) return false;
                  if (bookingCode && b.bookingCode === bookingCode) return false;
                  if (bookingGroupCode && (b.groupCode === bookingGroupCode || b.bookingCode === bookingGroupCode)) return false;
                  return true;
                })
              );
            };
            localStorage.setItem('nashgo_dev_student_bookings', purgeBookings(localStorage.getItem('nashgo_dev_student_bookings')));
            sessionStorage.setItem('nashgo_dev_student_bookings', purgeBookings(sessionStorage.getItem('nashgo_dev_student_bookings')));
          } else {
            // B) Soft-delete: Mark as DELETED so it transitions to "الطلبات المحذوفة"
            const markDeletedAttempts = (raw: string | null) => {
              if (!raw) return '[]';
              const list = JSON.parse(raw);
              return JSON.stringify(
                list.map((a: any) => {
                  const matches =
                    (paymentAttemptId && a.id === paymentAttemptId) ||
                    (bookingId && a.bookingId === bookingId) ||
                    (bookingGroupId && a.bookingGroupId === bookingGroupId) ||
                    (bookingCode && (a.bookingCode === bookingCode || a.bookingGroupCode === bookingCode));
                  if (matches) {
                    return { ...a, status: 'DELETED', deletedAt: nowIso, deletedBy: 'ADMIN' };
                  }
                  return a;
                })
              );
            };
            localStorage.setItem('oto_dev_payment_attempts', markDeletedAttempts(localStorage.getItem('oto_dev_payment_attempts')));
            sessionStorage.setItem('oto_dev_payment_attempts', markDeletedAttempts(sessionStorage.getItem('oto_dev_payment_attempts')));

            // Mark booking as CANCELLED in student bookings list
            const cancelBookings = (raw: string | null) => {
              if (!raw) return '[]';
              const list = JSON.parse(raw);
              return JSON.stringify(
                list.map((b: any) => {
                  const matches =
                    (bookingId && b.id === bookingId) ||
                    (bookingGroupId && (b.bookingGroupId === bookingGroupId || b.groupId === bookingGroupId)) ||
                    (bookingCode && b.bookingCode === bookingCode) ||
                    (bookingGroupCode && (b.groupCode === bookingGroupCode || b.bookingCode === bookingGroupCode));
                  if (matches) {
                    return { ...b, status: 'CANCELLED', bookingStatus: 'CANCELLED', paymentStatus: 'CANCELLED', deletedAt: nowIso };
                  }
                  return b;
                })
              );
            };
            localStorage.setItem('nashgo_dev_student_bookings', cancelBookings(localStorage.getItem('nashgo_dev_student_bookings')));
            sessionStorage.setItem('nashgo_dev_student_bookings', cancelBookings(sessionStorage.getItem('nashgo_dev_student_bookings')));
          }

          // Broadcast sync event immediately
          window.dispatchEvent(
            new CustomEvent('oto_booking_deleted', {
              detail: { paymentAttemptId, bookingId, bookingGroupId, bookingCode, bookingGroupCode, isPermanent },
            })
          );
          window.dispatchEvent(new CustomEvent('oto_data_sync'));
          window.dispatchEvent(new Event('storage'));
        } catch (storErr) {
          console.warn('Storage sync failed:', storErr);
        }
      }

      // Helper function to release a seat on a trip safely
      const releaseSeat = async (tId: string, sNum: number | string) => {
        if (!tId || sNum === null || sNum === undefined || sNum === '') return;
        try {
          const cleanSeatNum = String(sNum).replace('#', '').trim();
          const seatRef = doc(db, 'trips', tId, 'seats', cleanSeatNum);
          await updateDoc(seatRef, {
            status: 'AVAILABLE',
            bookingId: null,
            studentId: null,
            updatedAt: nowIso,
          }).catch(async () => {
            await deleteDoc(seatRef).catch(() => {});
          });
        } catch (_) {}
        try {
          const tripRef = doc(db, 'trips', tId);
          const tSnap = await getDoc(tripRef);
          if (tSnap && tSnap.exists()) {
            const cur = tSnap.data()?.availableSeats || 0;
            const maxCap = tSnap.data()?.totalSeats || tSnap.data()?.capacity || 50;
            const newAvail = Math.min(maxCap, cur + 1);
            await updateDoc(tripRef, {
              availableSeats: newAvail,
              availableSeatsCount: newAvail,
              status: 'OPEN',
              updatedAt: nowIso,
            }).catch(() => {});
          }
        } catch (_) {}
        invalidateOccupiedSeatsCache(tId);
      };

      // 3. Detect parent group if bookingId was passed
      if (!bookingGroupId && bookingId) {
        try {
          const bSnap = await getDoc(doc(db, 'bookings', bookingId));
          if (bSnap && bSnap.exists()) {
            const bData = bSnap.data() as any;
            if (bData.bookingGroupId) {
              bookingGroupId = bData.bookingGroupId;
            }
          }
        } catch (_) {}
      }

      // A) Handle Booking Group (Cancels or Deletes entire group & all child trips)
      if (bookingGroupId) {
        try {
          const gSnap = await getDoc(doc(db, 'bookingGroups', bookingGroupId));
          if (gSnap && gSnap.exists()) {
            const gData = gSnap.data() as any;
            if (Array.isArray(gData.bookings)) {
              for (const sub of gData.bookings) {
                if (sub.tripId && sub.seatNumber) {
                  await releaseSeat(sub.tripId, sub.seatNumber);
                }
              }
            }
          }

          if (isPermanent) {
            await deleteDoc(doc(db, 'bookingGroups', bookingGroupId)).catch(() => {});
          } else {
            await setDoc(
              doc(db, 'bookingGroups', bookingGroupId),
              {
                status: 'CANCELLED',
                bookingStatus: 'CANCELLED',
                paymentStatus: 'CANCELLED',
                deletedAt: nowIso,
                deletedBy: 'ADMIN',
                updatedAt: nowIso,
              },
              { merge: true }
            );
          }

          // Child bookings in Firestore matching this group
          try {
            const childQuery = query(collection(db, 'bookings'), where('bookingGroupId', '==', bookingGroupId));
            const childSnaps = await getDocs(childQuery);
            if (childSnaps) {
              for (const cDoc of childSnaps.docs) {
                const cData = cDoc.data() as any;
                if (cData.tripId && cData.seatNumber) {
                  await releaseSeat(cData.tripId, cData.seatNumber);
                }
                if (isPermanent) {
                  await deleteDoc(cDoc.ref).catch(() => {});
                } else {
                  await setDoc(
                    cDoc.ref,
                    {
                      status: 'CANCELLED',
                      bookingStatus: 'CANCELLED',
                      paymentStatus: 'CANCELLED',
                      deletedAt: nowIso,
                      deletedBy: 'ADMIN',
                      updatedAt: nowIso,
                    },
                    { merge: true }
                  );
                }
              }
            }
          } catch (_) {}

          // Also mark/delete all payment attempts associated with this group
          try {
            const paQuery = query(collection(db, 'paymentAttempts'), where('bookingGroupId', '==', bookingGroupId));
            const paSnaps = await getDocs(paQuery);
            if (paSnaps) {
              for (const paDoc of paSnaps.docs) {
                if (isPermanent) {
                  await deleteDoc(paDoc.ref).catch(() => {});
                } else {
                  await setDoc(
                    paDoc.ref,
                    {
                      status: 'DELETED',
                      deletedAt: nowIso,
                      deletedBy: 'ADMIN',
                      updatedAt: nowIso,
                    },
                    { merge: true }
                  );
                }
              }
            }
          } catch (_) {}
        } catch (gErr) {
          console.warn('BookingGroup update failed in deletePaymentAttempt:', gErr);
        }
      }

      // B) Handle Individual Booking
      if (bookingId) {
        try {
          const bSnap = await getDoc(doc(db, 'bookings', bookingId));
          if (bSnap && bSnap.exists()) {
            const bData = bSnap.data() as any;
            const bTripId = tripId || bData.tripId;
            const bSeat = seatNumber || bData.seatNumber;
            if (bTripId && bSeat) {
              await releaseSeat(bTripId, bSeat);
            }
          }
          if (isPermanent) {
            await deleteDoc(doc(db, 'bookings', bookingId)).catch(() => {});
          } else {
            await setDoc(
              doc(db, 'bookings', bookingId),
              {
                status: 'CANCELLED',
                bookingStatus: 'CANCELLED',
                paymentStatus: 'CANCELLED',
                deletedAt: nowIso,
                deletedBy: 'ADMIN',
                updatedAt: nowIso,
              },
              { merge: true }
            );
          }

          // Associated payment attempts for this booking
          try {
            const paQuery = query(collection(db, 'paymentAttempts'), where('bookingId', '==', bookingId));
            const paSnaps = await getDocs(paQuery);
            for (const paDoc of paSnaps.docs) {
              if (isPermanent) {
                await deleteDoc(paDoc.ref).catch(() => {});
              } else {
                await setDoc(
                  paDoc.ref,
                  {
                    status: 'DELETED',
                    deletedAt: nowIso,
                    deletedBy: 'ADMIN',
                    updatedAt: nowIso,
                  },
                  { merge: true }
                );
              }
            }
          } catch (_) {}
        } catch (bErr) {
          console.warn('Booking status update failed:', bErr);
        }
      }

      // C) Direct seat release if known
      if (tripId && seatNumber) {
        await releaseSeat(tripId, seatNumber);
      }

      // D) Handle Payment Attempt document
      if (paymentAttemptId) {
        try {
          if (isPermanent) {
            await deleteDoc(doc(db, 'paymentAttempts', paymentAttemptId)).catch(() => {});
          } else {
            await setDoc(
              doc(db, 'paymentAttempts', paymentAttemptId),
              {
                status: 'DELETED',
                deletedAt: nowIso,
                deletedBy: 'ADMIN',
                updatedAt: nowIso,
              },
              { merge: true }
            );
          }
        } catch (fsErr) {
          console.warn('paymentAttempts doc status update failed:', fsErr);
        }
      }

      invalidatePaymentAttemptsCache();
      invalidateOccupiedSeatsCache();
      invalidateAdminBookingsCache();
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('oto_data_sync'));
      }

      return {
        success: true,
        data: {
          messageAr: isPermanent
            ? `تم حذف الحجز وسجل الدفع (${bookingCode || bookingGroupCode || paymentAttemptId}) نهائياً وتحرير المقاعد.`
            : `تم نقل طلب الحجز (${bookingCode || bookingGroupCode || paymentAttemptId}) إلى قسم الطلبات المحذوفة وتحرير المقاعد بنجاح.`,
        },
      };
    } catch (err: any) {
      console.error('deletePaymentAttempt error:', err);
      return { success: false, error: createError('UNKNOWN_ERROR', err?.message || 'فشل حذف الحجز وسجل الدفع') };
    }
  },

  /**
   * 6. Upload Payment Proof Image to Firebase Storage
   */
  async uploadPaymentProof(
    studentId: string,
    targetId: string,
    file: File
  ): Promise<ApiResponse<{ downloadUrl: string; storagePath: string }>> {
    let fileToUpload = file;
    let fallbackDataUrl = '';

    try {
      const compressed = await compressPaymentProof(file, 1000, 0.75);
      fileToUpload = compressed.file;
      fallbackDataUrl = compressed.dataUrl;
    } catch (cErr) {
      console.warn('Pre-compression fallback:', cErr);
    }

    try {
      const fileExt = fileToUpload.name.split('.').pop() || 'jpg';
      const cleanFileName = `proof_${Date.now()}.${fileExt}`;
      const storagePath = `payment-proofs/${studentId}/${targetId}/${cleanFileName}`;
      const storageRef = ref(storage, storagePath);

      const metadata = {
        contentType: fileToUpload.type || 'image/jpeg',
        customMetadata: {
          studentId,
          targetId,
          uploadedAt: new Date().toISOString(),
        },
      };

      const uploadWithTimeout = Promise.race([
        (async () => {
          await uploadBytes(storageRef, fileToUpload, metadata);
          return await getDownloadURL(storageRef);
        })(),
        new Promise<string>((_, reject) =>
          setTimeout(() => reject(new Error('Firebase Storage timeout/not initialized')), 1200)
        ),
      ]);

      const downloadUrl = await uploadWithTimeout;

      return {
        success: true,
        data: { downloadUrl, storagePath },
      };
    } catch (err: any) {
      console.warn('Firebase Storage upload failed, falling back to direct compressed Base64:', err);
      if (fallbackDataUrl) {
        return {
          success: true,
          data: {
            downloadUrl: fallbackDataUrl,
            storagePath: `firestore-direct-compressed/${studentId}/${Date.now()}`,
          },
        };
      }
      try {
        const dataUrl = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
        return {
          success: true,
          data: {
            downloadUrl: dataUrl,
            storagePath: `fallback-data-url/${studentId}/${Date.now()}`,
          },
        };
      } catch (readErr: any) {
        return {
          success: false,
          error: createError('NETWORK_ERROR', err?.message || 'تعذر معالجة صورة إثبات التحويل'),
        };
      }
    }
  },

  /**
   * 7. Fetch Payment Settings (Firestore or Defaults)
   */
  async getPaymentSettings(): Promise<ApiResponse<PaymentSettings>> {
    try {
      const docRef = doc(db, 'settings', 'payment');
      const snap = await getDoc(docRef);
      if (snap.exists()) {
        const firestoreData = snap.data();
        const merged = { ...DEFAULT_CLIENT_PAYMENT_SETTINGS, ...firestoreData } as PaymentSettings;
        if (typeof window !== 'undefined') {
          try {
            localStorage.setItem('oto_payment_settings', JSON.stringify(merged));
          } catch (_) {}
        }
        return { success: true, data: merged };
      }
    } catch (err: any) {
      console.warn('getPaymentSettings Firestore read fallback to local cache:', err);
    }

    if (typeof window !== 'undefined') {
      try {
        const cached = localStorage.getItem('oto_payment_settings');
        if (cached) {
          const parsed = JSON.parse(cached);
          return {
            success: true,
            data: { ...DEFAULT_CLIENT_PAYMENT_SETTINGS, ...parsed } as PaymentSettings,
          };
        }
      } catch (_) {}
    }

    return { success: true, data: DEFAULT_CLIENT_PAYMENT_SETTINGS };
  },

  /**
   * 7.1 Realtime listener for Payment Settings (Firestore snapshot + LocalStorage + CustomEvents)
   */
  subscribeToPaymentSettings(callback: (settings: PaymentSettings) => void): () => void {
    let unsubFirestore: (() => void) | null = null;
    try {
      const docRef = doc(db, 'settings', 'payment');
      unsubFirestore = onSnapshot(
        docRef,
        (snap) => {
          if (snap.exists()) {
            const data = snap.data();
            const merged = { ...DEFAULT_CLIENT_PAYMENT_SETTINGS, ...data } as PaymentSettings;
            if (typeof window !== 'undefined') {
              try {
                localStorage.setItem('oto_payment_settings', JSON.stringify(merged));
              } catch (_) {}
            }
            callback(merged);
          }
        },
        (err) => {
          console.warn('onSnapshot error on payment settings:', err);
        }
      );
    } catch (err) {
      console.warn('Could not attach Firestore onSnapshot for payment settings:', err);
    }

    const handleCustomEvent = (e: Event) => {
      const customEvent = e as CustomEvent<PaymentSettings>;
      if (customEvent.detail) {
        callback(customEvent.detail);
      }
    };

    const handleStorageEvent = (e: StorageEvent) => {
      if (e.key === 'oto_payment_settings' && e.newValue) {
        try {
          const parsed = JSON.parse(e.newValue);
          callback({ ...DEFAULT_CLIENT_PAYMENT_SETTINGS, ...parsed });
        } catch (_) {}
      }
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('oto_payment_settings_changed', handleCustomEvent);
      window.addEventListener('storage', handleStorageEvent);
    }

    return () => {
      if (unsubFirestore) {
        unsubFirestore();
      }
      if (typeof window !== 'undefined') {
        window.removeEventListener('oto_payment_settings_changed', handleCustomEvent);
        window.removeEventListener('storage', handleStorageEvent);
      }
    };
  },

  /**
   * 8. Admin Updates Payment Settings
   */
  async updatePaymentSettings(
    settings: Partial<PaymentSettings>
  ): Promise<ApiResponse<{ messageAr: string }>> {
    try {
      // 1. Authoritative direct Firestore write
      const docRef = doc(db, 'settings', 'payment');
      await setDoc(docRef, { ...settings, updatedAt: new Date().toISOString() }, { merge: true });

      // 2. Cache in localStorage merged and dispatch event for immediate sync across tabs/components
      if (typeof window !== 'undefined') {
        try {
          const cached = localStorage.getItem('oto_payment_settings');
          const current = cached ? JSON.parse(cached) : DEFAULT_CLIENT_PAYMENT_SETTINGS;
          const merged = { ...DEFAULT_CLIENT_PAYMENT_SETTINGS, ...current, ...settings };
          localStorage.setItem('oto_payment_settings', JSON.stringify(merged));
          window.dispatchEvent(new CustomEvent('oto_payment_settings_changed', { detail: merged }));
        } catch (_) {}
      }

      return {
        success: true,
        data: { messageAr: 'تم حفظ وتحديث إعدادات وسائل الدفع بنجاح في قاعدة البيانات' },
      };
    } catch (err: any) {
      console.warn('Firestore setDoc failed, saving to local offline storage:', err);
      if (typeof window !== 'undefined') {
        try {
          const cached = localStorage.getItem('oto_payment_settings');
          const current = cached ? JSON.parse(cached) : DEFAULT_CLIENT_PAYMENT_SETTINGS;
          const merged = { ...DEFAULT_CLIENT_PAYMENT_SETTINGS, ...current, ...settings };
          localStorage.setItem('oto_payment_settings', JSON.stringify(merged));
          window.dispatchEvent(new CustomEvent('oto_payment_settings_changed', { detail: merged }));
        } catch (_) {}
      }
      return {
        success: true,
        data: { messageAr: 'تم حفظ التعديلات بنجاح' },
      };
    }
  },

  /**
   * 9. Fetch payment attempt document (Read-only for student owner or admin)
   */
  async getPaymentAttempt(paymentAttemptId: string): Promise<ApiResponse<PaymentAttempt>> {
    try {
      const docRef = doc(db, 'paymentAttempts', paymentAttemptId);
      const snap = await getDoc(docRef);
      if (!snap.exists()) {
        return { success: false, error: createError('UNKNOWN_ERROR', 'لم يتم العثور على سجل محاولة الدفع') };
      }
      return { success: true, data: snap.data() as PaymentAttempt };
    } catch (err: any) {
      console.error('getPaymentAttempt error:', err);
      return { success: false, error: createError('NETWORK_ERROR', err?.message) };
    }
  },

  /**
   * 10. Fetch booking with authoritative payment status
   */
  async getBooking(bookingId: string): Promise<ApiResponse<Booking>> {
    try {
      const docRef = doc(db, 'bookings', bookingId);
      const snap = await getDoc(docRef);
      if (snap.exists()) {
        return { success: true, data: snap.data() as Booking };
      }
    } catch (err: any) {
      console.warn('getBooking Firestore read error, checking dev fallback:', err);
    }

    // Fallback for local cached bookings
    if (typeof window !== 'undefined') {
      try {
        const raw = sessionStorage.getItem(`nashgo_dev_booking_${bookingId}`) || localStorage.getItem(`nashgo_dev_booking_${bookingId}`);
        if (raw) {
          return { success: true, data: JSON.parse(raw) as Booking };
        }
      } catch (e) {
        // Ignored
      }
    }

    return {
      success: false,
      error: createError('BOOKING_NOT_FOUND', 'لم يتم العثور على بيانات الحجز'),
    };
  },

  /**
   * 10.1 Fetch booking group with authoritative payment status
   */
  async getBookingGroup(groupId: string): Promise<ApiResponse<BookingGroup>> {
    try {
      const docRef = doc(db, 'bookingGroups', groupId);
      const snap = await getDoc(docRef);
      if (snap.exists()) {
        return { success: true, data: snap.data() as BookingGroup };
      }
    } catch (err: any) {
      console.warn('getBookingGroup Firestore read error, checking fallback:', err);
    }

    // Fallback for local cached booking groups
    if (typeof window !== 'undefined') {
      try {
        const raw =
          sessionStorage.getItem(`nashgo_dev_group_${groupId}`) ||
          localStorage.getItem(`nashgo_dev_group_${groupId}`);
        if (raw) {
          return { success: true, data: JSON.parse(raw) as BookingGroup };
        }
      } catch (e) {
        // Ignored
      }
    }

    return {
      success: false,
      error: createError('BOOKING_NOT_FOUND', 'لم يتم العثور على بيانات مجموعة الحجز'),
    };
  },

  /**
   * 11. Admin: Fetch All Pending Manual Payments (Unified with getAllPaymentAttempts for 100% data consistency)
   */
  async getPendingManualPayments(forceRefresh = false): Promise<ApiResponse<PaymentAttempt[]>> {
    const res = await this.getAllPaymentAttempts(undefined, forceRefresh);
    if (!res.success || !res.data) {
      return res;
    }
    const pendingItems = res.data.filter(
      (a) => a.status === 'PENDING_VERIFICATION' || a.status === 'AWAITING_BRANCH_PAYMENT'
    );
    return { success: true, data: pendingItems };
  },

  /**
   * 12. Admin: Fetch All Payment Attempts with Optional Status Filter
   * Optimized with in-memory caching and batching to avoid server slowdown during request surges
   */
  async getAllPaymentAttempts(
    statusFilter?: string,
    forceRefresh = false
  ): Promise<ApiResponse<PaymentAttempt[]>> {
    if (!forceRefresh && cachedPaymentAttempts && cachedPaymentAttempts.filter === statusFilter && (Date.now() - cachedPaymentAttempts.timestamp < 12000)) {
      return { success: true, data: cachedPaymentAttempts.data };
    }

    const items: PaymentAttempt[] = [];
    try {
      const colRef = collection(db, 'paymentAttempts');
      let q;
      if (statusFilter && statusFilter !== 'ALL') {
        try {
          q = query(colRef, where('status', '==', statusFilter), orderBy('createdAt', 'desc'));
          const snap = await getDocs(q);
          snap.forEach((d) => items.push(d.data() as PaymentAttempt));
        } catch (idxErr) {
          // Fallback if composite index is pending
          q = query(colRef, where('status', '==', statusFilter));
          const snap = await getDocs(q);
          snap.forEach((d) => items.push(d.data() as PaymentAttempt));
        }
      } else {
        try {
          q = query(colRef, orderBy('createdAt', 'desc'));
          const snap = await getDocs(q);
          snap.forEach((d) => {
            const data = d.data() as any;
            if (data.status !== 'DELETED' && !data.deletedAt) {
              items.push(data as PaymentAttempt);
            }
          });
        } catch (idxErr) {
          const snap = await getDocs(colRef);
          snap.forEach((d) => {
            const data = d.data() as any;
            if (data.status !== 'DELETED' && !data.deletedAt) {
              items.push(data as PaymentAttempt);
            }
          });
        }
      }
    } catch (err: any) {
      console.warn('getAllPaymentAttempts Firestore error:', err);
    }

    // Fallback & Auto-heal: Check bookingGroups with pending payment, branch, or proof
    // Only synthesize for active groups; NEVER resurrect deleted or cancelled groups!
    if (statusFilter !== 'DELETED') {
      try {
        const candidateStatuses = ['PENDING_VERIFICATION', 'AWAITING_TRANSFER', 'AWAITING_BRANCH_PAYMENT'];
        let bgDocs: any[] = [];
        try {
          const bgQuery = query(
            collection(db, 'bookingGroups'),
            where('paymentStatus', 'in', candidateStatuses),
            limit(50)
          );
          const bgSnap = await getDocs(bgQuery);
          bgDocs = bgSnap.docs;
        } catch (inErr) {
          const qPending = query(collection(db, 'bookingGroups'), where('paymentStatus', '==', 'PENDING_VERIFICATION'), limit(30));
          const qAwaiting = query(collection(db, 'bookingGroups'), where('paymentStatus', '==', 'AWAITING_TRANSFER'), limit(30));
          const [sPending, sAwaiting] = await Promise.all([getDocs(qPending), getDocs(qAwaiting)]);
          const seen = new Set<string>();
          for (const d of [...sPending.docs, ...sAwaiting.docs]) {
            if (!seen.has(d.id)) {
              seen.add(d.id);
              bgDocs.push(d);
            }
          }
        }

        for (const d of bgDocs) {
          const g = d.data() as any;
          if (g.status === 'CANCELLED' || g.status === 'DELETED' || g.deletedAt || g.paymentStatus === 'CANCELLED') {
            continue;
          }
          bookingGroupCache.set(d.id, g);
          const code = g.groupCode || d.id;
          const exists = items.some(
            (it) => it.bookingGroupId === d.id || it.bookingGroupCode === code || it.bookingCode === code
          );
          if (!exists) {
            let sName = g.studentSnapshot?.fullName;
            if (isGenericStudentName(sName) && g.studentId) {
              sName = studentProfileCache.get(g.studentId)?.fullName;
            }
            const sPhone = g.studentSnapshot?.phoneNumber || g.studentSnapshot?.phone || '';
            const isBranch = g.paymentMethod === 'BRANCH' || g.paymentStatus === 'AWAITING_BRANCH_PAYMENT';
            const synthesized: PaymentAttempt = {
              id: g.paymentAttemptId || `ATT-${d.id.slice(0, 10).toUpperCase()}`,
              studentId: g.studentId || 'unknown',
              studentName: sName && !isGenericStudentName(sName) ? sName : (sPhone ? `طالب (${sPhone})` : 'طالب مسجل'),
              studentPhone: sPhone,
              universityName: g.studentSnapshot?.universityName || g.universityName || 'الجامعة',
              tripDate: g.tripDates?.join(' ، ') || g.bookings?.map((b: any) => b.date).join(' ، ') || '',
              seatNumber: g.bookings?.map((b: any) => '#' + b.seatNumber).join(', ') || (g.itemCount ? `${g.itemCount} مقاعد` : ''),
              amount: g.totalAmount || g.price || 0,
              currency: 'EGP',
              provider: 'MANUAL',
              expiresAt: new Date(Date.now() + 86400000).toISOString(),
              bookingCode: code,
              bookingGroupCode: code,
              bookingGroupId: d.id,
              paymentMethod: g.paymentMethod || 'INSTAPAY',
              transferFrom: g.transferFrom || '',
              proofImageUrl: g.proofImageUrl || g.proofImageBase64 || null,
              proofImageBase64: g.proofImageBase64 || g.proofImageUrl || null,
              status: isBranch ? 'AWAITING_BRANCH_PAYMENT' : 'PENDING_VERIFICATION',
              createdAt: g.createdAt || new Date().toISOString(),
              updatedAt: g.updatedAt || new Date().toISOString(),
            };
            items.push(synthesized);
          }
        }
      } catch (bgErr) {
        console.warn('getAllPaymentAttempts bookingGroups fallback error:', bgErr);
      }

      // Fallback & Auto-heal: Check individual bookings with pending payment or proof
      try {
        const candidateStatuses = ['PENDING_VERIFICATION', 'AWAITING_TRANSFER', 'AWAITING_BRANCH_PAYMENT'];
        let bDocs: any[] = [];
        try {
          const bQuery = query(
            collection(db, 'bookings'),
            where('paymentStatus', 'in', candidateStatuses),
            limit(50)
          );
          const bSnap = await getDocs(bQuery);
          bDocs = bSnap.docs;
        } catch (inErr) {
          const qPending = query(collection(db, 'bookings'), where('paymentStatus', '==', 'PENDING_VERIFICATION'), limit(30));
          const qAwaiting = query(collection(db, 'bookings'), where('paymentStatus', '==', 'AWAITING_TRANSFER'), limit(30));
          const [sPending, sAwaiting] = await Promise.all([getDocs(qPending), getDocs(qAwaiting)]);
          const seen = new Set<string>();
          for (const d of [...sPending.docs, ...sAwaiting.docs]) {
            if (!seen.has(d.id)) {
              seen.add(d.id);
              bDocs.push(d);
            }
          }
        }

        for (const d of bDocs) {
          const b = d.data() as any;
          if (b.bookingGroupId || b.status === 'CANCELLED' || b.status === 'DELETED' || b.deletedAt || b.paymentStatus === 'CANCELLED') {
            continue;
          }
          bookingCache.set(d.id, b);
          const code = b.bookingCode || d.id;
          const exists = items.some((it) => it.bookingId === d.id || it.bookingCode === code);
          if (!exists) {
            let sName = b.studentSnapshot?.fullName || b.studentName;
            if (isGenericStudentName(sName) && b.studentId) {
              sName = studentProfileCache.get(b.studentId)?.fullName;
            }
            const sPhone = b.studentSnapshot?.phoneNumber || b.phoneNumber || '';
            const isBranch = b.paymentMethod === 'BRANCH' || b.paymentStatus === 'AWAITING_BRANCH_PAYMENT';
            const synthesized: PaymentAttempt = {
              id: b.paymentAttemptId || `ATT-${d.id.slice(0, 10).toUpperCase()}`,
              studentId: b.studentId || 'unknown',
              studentName: sName && !isGenericStudentName(sName) ? sName : (sPhone ? `طالب (${sPhone})` : 'طالب مسجل'),
              studentPhone: sPhone,
              universityName: b.universityName || 'الجامعة',
              tripDate: b.tripDate || '',
              seatNumber: b.seatNumber || '',
              amount: b.price || 0,
              currency: 'EGP',
              provider: 'MANUAL',
              expiresAt: new Date(Date.now() + 86400000).toISOString(),
              bookingCode: code,
              bookingId: d.id,
              paymentMethod: b.paymentMethod || 'INSTAPAY',
              transferFrom: b.transferFrom || '',
              proofImageUrl: b.proofImageUrl || b.proofImageBase64 || null,
              proofImageBase64: b.proofImageBase64 || b.proofImageUrl || null,
              status: isBranch ? 'AWAITING_BRANCH_PAYMENT' : 'PENDING_VERIFICATION',
              createdAt: b.createdAt || new Date().toISOString(),
              updatedAt: b.updatedAt || new Date().toISOString(),
            };
            items.push(synthesized);
          }
        }
      } catch (bErr) {
        console.warn('getAllPaymentAttempts bookings fallback error:', bErr);
      }
    }

    // Merge dev/local storage attempts
    if (typeof window !== 'undefined') {
      try {
        const rawLocal = localStorage.getItem('oto_dev_payment_attempts') || sessionStorage.getItem('oto_dev_payment_attempts');
        if (rawLocal) {
          const localList = JSON.parse(rawLocal) as PaymentAttempt[];
          for (const lItem of localList) {
            if (statusFilter !== 'DELETED' && (lItem.status === 'DELETED' || (lItem as any).deletedAt)) {
              continue;
            }
            const matchesStatus = !statusFilter || statusFilter === 'ALL' || lItem.status === statusFilter;
            if (matchesStatus && !items.some((i) => i.id === lItem.id)) {
              items.push(lItem);
            }
          }
        }
      } catch (_) {}
    }

    // -------------------------------------------------------------
    // CONSOLIDATION PASS: Multi-day & package booking groups unified procedure
    // Groups all child trips of a package into ONE single procedure of total amount
    // -------------------------------------------------------------
    try {
      const itemsByGroup = new Map<string, PaymentAttempt[]>();
      const nonGroupItems: PaymentAttempt[] = [];

      // Link any child items that belong to a group
      for (const item of items) {
        let gId = item.bookingGroupId;
        if (!gId && item.bookingId && bookingCache.has(item.bookingId)) {
          gId = bookingCache.get(item.bookingId)?.bookingGroupId;
          if (gId) item.bookingGroupId = gId;
        }
        if (gId) {
          if (!itemsByGroup.has(gId)) {
            itemsByGroup.set(gId, []);
          }
          itemsByGroup.get(gId)!.push(item);
        } else {
          nonGroupItems.push(item);
        }
      }

      // Consolidate any booking groups present
      if (itemsByGroup.size > 0) {
        const consolidatedGroups: PaymentAttempt[] = [];
        for (const [gId, childAttempts] of itemsByGroup.entries()) {
          let gData = bookingGroupCache.get(gId);
          if (!gData) {
            try {
              const gSnap = await getDoc(doc(db, 'bookingGroups', gId));
              if (gSnap.exists()) {
                gData = gSnap.data() as any;
                bookingGroupCache.set(gId, gData);
              }
            } catch (_) {}
          }

          // If group is deleted or cancelled, do not resurrect as active
          if (statusFilter !== 'DELETED' && (gData?.status === 'CANCELLED' || gData?.status === 'DELETED' || gData?.deletedAt)) {
            continue;
          }

          let sName = gData?.studentSnapshot?.fullName;
          if (isGenericStudentName(sName) && gData?.studentId) {
            sName = studentProfileCache.get(gData.studentId)?.fullName;
          }
          if (!sName || isGenericStudentName(sName)) {
            sName = childAttempts.find((a) => !isGenericStudentName(a.studentName))?.studentName || 'طالب مسجل';
          }
          const sPhone =
            gData?.studentSnapshot?.phoneNumber ||
            gData?.studentSnapshot?.phone ||
            childAttempts.find((a) => a.studentPhone)?.studentPhone ||
            '';

          const groupTotalAmount =
            gData?.totalAmount ||
            gData?.price ||
            childAttempts.reduce((sum, ca) => sum + (ca.amount || 0), 0) ||
            1000;
          const tripCount = gData?.itemCount || gData?.bookings?.length || childAttempts.length || 8;
          const groupDates =
            gData?.tripDates?.join(' ، ') ||
            gData?.bookings?.map((b: any) => b.date).join(' ، ') ||
            childAttempts.map((ca) => ca.tripDate).filter(Boolean).join(' ، ');
          const groupSeats =
            (gData?.bookings?.map((b: any) => '#' + b.seatNumber).join(', ') as any) ||
            `${tripCount} مقاعد (مجموعة)`;
          const gMethod = gData?.paymentMethod || childAttempts[0]?.paymentMethod || 'INSTAPAY';
          const gTransferFrom =
            gData?.transferFrom || childAttempts.find((ca) => ca.transferFrom)?.transferFrom || '';
          const gProofUrl =
            gData?.proofImageUrl ||
            gData?.proofImageBase64 ||
            childAttempts.find((ca) => ca.proofImageUrl)?.proofImageUrl ||
            null;
          const gProofBase64 =
            gData?.proofImageBase64 ||
            gData?.proofImageUrl ||
            childAttempts.find((ca) => ca.proofImageBase64)?.proofImageBase64 ||
            null;

          const unifiedAttempt: PaymentAttempt = {
            id: gData?.paymentAttemptId || `ATT-GRP-${gId.slice(0, 10).toUpperCase()}`,
            studentId: gData?.studentId || childAttempts[0]?.studentId || 'unknown',
            studentName: sName,
            studentPhone: sPhone,
            universityName:
              gData?.studentSnapshot?.universityName ||
              gData?.universityName ||
              childAttempts[0]?.universityName ||
              'الجامعة',
            tripDate: groupDates,
            seatNumber: groupSeats,
            amount: groupTotalAmount,
            currency: 'EGP',
            provider: 'MANUAL',
            expiresAt: new Date(Date.now() + 86400000).toISOString(),
            bookingCode: gData?.groupCode || gId,
            bookingGroupCode: gData?.groupCode || gId,
            bookingGroupId: gId,
            paymentMethod: gMethod,
            transferFrom: gTransferFrom,
            proofImageUrl: gProofUrl,
            proofImageBase64: gProofBase64,
            status: gData?.paymentStatus || childAttempts[0]?.status || 'PENDING_VERIFICATION',
            tripCount,
            reviewedByName: gData?.reviewedByName || childAttempts[0]?.reviewedByName || gData?.verifiedByName || childAttempts[0]?.verifiedByName || gData?.rejectedByName || childAttempts[0]?.rejectedByName || null,
            verifiedByName: gData?.verifiedByName || childAttempts[0]?.verifiedByName || null,
            rejectedByName: gData?.rejectedByName || childAttempts[0]?.rejectedByName || null,
            reviewedAt: gData?.reviewedAt || childAttempts[0]?.reviewedAt || null,
            verifiedAt: gData?.verifiedAt || childAttempts[0]?.verifiedAt || null,
            rejectedAt: gData?.rejectedAt || childAttempts[0]?.rejectedAt || null,
            adminNote: gData?.adminNote || childAttempts[0]?.adminNote || null,
            rejectionReason: gData?.rejectionReason || childAttempts[0]?.rejectionReason || null,
            createdAt: gData?.createdAt || childAttempts[0]?.createdAt || new Date().toISOString(),
            updatedAt: gData?.updatedAt || childAttempts[0]?.updatedAt || new Date().toISOString(),
          };

          consolidatedGroups.push(unifiedAttempt);

          // Persist unified attempt only if it is active and not marked DELETED
          if (unifiedAttempt.status !== 'DELETED' && !(unifiedAttempt as any).deletedAt) {
            try {
              setDoc(doc(db, 'paymentAttempts', unifiedAttempt.id), unifiedAttempt, { merge: true }).catch(() => {});
              for (const ca of childAttempts) {
                if (ca.id !== unifiedAttempt.id) {
                  deleteDoc(doc(db, 'paymentAttempts', ca.id)).catch(() => {});
                }
              }
            } catch (_) {}
          }
        }

        // Rebuild items list with single unified group attempts + non-group items
        items.length = 0;
        items.push(...nonGroupItems, ...consolidatedGroups);
      }
    } catch (cErr) {
      console.warn('Group consolidation error in getAllPaymentAttempts:', cErr);
    }

    // 1. Gather all student IDs that need profile resolution (where name is generic or missing)
    const missingStudentIds = new Set<string>();
    for (const item of items) {
      if (isGenericStudentName(item.studentName)) {
        if (item.studentId && item.studentId !== 'unknown' && item.studentId !== 'student' && item.studentId !== 'current-student') {
          if (studentProfileCache.has(item.studentId)) {
            const cached = studentProfileCache.get(item.studentId)!;
            if (cached.fullName && !isGenericStudentName(cached.fullName)) {
              item.studentName = cached.fullName;
              if (!item.studentPhone && cached.phoneNumber) item.studentPhone = cached.phoneNumber;
            }
          } else {
            missingStudentIds.add(item.studentId);
          }
        }
      }
    }

    // 2. Batch-fetch student profiles in parallel (up to 30 at a time) to prevent sequential lag
    if (missingStudentIds.size > 0) {
      const uids = Array.from(missingStudentIds).slice(0, 30);
      await Promise.allSettled(
        uids.map(async (uid) => {
          try {
            const sSnap = await getDoc(doc(db, 'students', uid));
            if (sSnap.exists()) {
              const sData = sSnap.data() as any;
              studentProfileCache.set(uid, {
                fullName: sData.fullName || '',
                phoneNumber: sData.phoneNumber || '',
                universityName: sData.universityName || '',
              });
            }
          } catch (_) {}
        })
      );

      // Apply newly resolved names and persist them to Firestore paymentAttempts
      for (const item of items) {
        if (isGenericStudentName(item.studentName) && item.studentId && studentProfileCache.has(item.studentId)) {
          const cached = studentProfileCache.get(item.studentId)!;
          if (cached.fullName && !isGenericStudentName(cached.fullName)) {
            item.studentName = cached.fullName;
            if (!item.studentPhone && cached.phoneNumber) item.studentPhone = cached.phoneNumber;

            // Auto-heal in Firestore asynchronously so future queries read the real name immediately
            if (item.status !== 'DELETED') {
              updateDoc(doc(db, 'paymentAttempts', item.id), {
                studentName: cached.fullName,
                ...(cached.phoneNumber ? { studentPhone: cached.phoneNumber } : {}),
              }).catch(() => {});
            }
          }
        }
      }
    }

    // 3. Batch-enrich any missing bookingGroup or booking details in parallel
    const itemsNeedingGroupInfo = items.filter(
      (it) => it.bookingGroupId && (!it.amount || !it.universityName || !it.proofImageUrl || isGenericStudentName(it.studentName))
    );
    if (itemsNeedingGroupInfo.length > 0) {
      const uniqueGroupIds = Array.from(new Set(itemsNeedingGroupInfo.map((it) => it.bookingGroupId!)));
      await Promise.allSettled(
        uniqueGroupIds.map(async (gId) => {
          if (!bookingGroupCache.has(gId)) {
            try {
              const gSnap = await getDoc(doc(db, 'bookingGroups', gId));
              if (gSnap.exists()) {
                bookingGroupCache.set(gId, gSnap.data());
              }
            } catch (_) {}
          }
        })
      );

      for (const item of itemsNeedingGroupInfo) {
        const gData = bookingGroupCache.get(item.bookingGroupId!);
        if (gData) {
          if (isGenericStudentName(item.studentName) && gData.studentSnapshot?.fullName && !isGenericStudentName(gData.studentSnapshot.fullName)) {
            item.studentName = gData.studentSnapshot.fullName;
          }
          item.studentPhone = item.studentPhone || gData.studentSnapshot?.phoneNumber;
          item.universityName = item.universityName || gData.studentSnapshot?.universityName || gData.universityName;
          item.tripDate = item.tripDate || gData.tripDates?.join(' ، ') || gData.bookings?.map((b: any) => b.date).join(' ، ');
          item.seatNumber = item.seatNumber || (gData.bookings?.length ? gData.bookings.map((b: any) => '#' + b.seatNumber).join(', ') : gData.itemCount);
          item.amount = item.amount || gData.totalAmount || gData.price || 0;
          item.bookingGroupCode = item.bookingGroupCode || gData.groupCode;
          item.bookingCode = item.bookingCode || gData.groupCode;
          item.proofImageUrl = item.proofImageUrl || gData.proofImageUrl || gData.proofImageBase64 || null;
          item.proofImageBase64 = item.proofImageBase64 || gData.proofImageBase64 || gData.proofImageUrl || null;
        }
      }
    }

    // 4. Ensure no item ever displays generic "طالب OTO المعتمد"
    for (const item of items) {
      if (isGenericStudentName(item.studentName)) {
        item.studentName = item.studentPhone
          ? `طالب (${item.studentPhone})`
          : item.bookingCode
          ? `طالب (${item.bookingCode})`
          : 'طالب مسجل';
      }
    }

    // 5. Strictly sort all items: Newest Created First (Chronological order)
    items.sort((a, b) => {
      const timeA = new Date(a.createdAt || a.submittedAt || 0).getTime();
      const timeB = new Date(b.createdAt || b.submittedAt || 0).getTime();
      return timeB - timeA;
    });

    // 6. Guarantee that deleted items NEVER appear in normal views unless specifically filtering for DELETED
    const finalItems = items.filter((item) => {
      if (statusFilter === 'DELETED') {
        return item.status === 'DELETED' || !!(item as any).deletedAt;
      }
      return item.status !== 'DELETED' && !(item as any).deletedAt;
    });

    cachedPaymentAttempts = { data: finalItems, filter: statusFilter, timestamp: Date.now() };
    return { success: true, data: finalItems };
  },
};
